import React, { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { AHText, AHTextFonts, Box, useTheme } from "arkh";
import { TeamScheduleListItemTimeOfDay_TeamScheduleEventOccasion$key } from "./__generated__/TeamScheduleListItemTimeOfDay_TeamScheduleEventOccasion.graphql";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import Color from "color";
import { StoreFormattedDate } from "../../../../common/dates/StoreFormattedDate";

export function TeamScheduleListItemTimeOfDay(props: {
  eventOccasion: TeamScheduleListItemTimeOfDay_TeamScheduleEventOccasion$key;
  status: "none" | "active" | "soon";
  tint: string;
}) {
  const eventOccasion = useFragment(
    graphql`
      fragment TeamScheduleListItemTimeOfDay_TeamScheduleEventOccasion on TeamScheduleEventOccasion {
        __typename
        ... on RoutineOccasion {
          startAtDate {
            ...StoreFormattedDate @arguments(style: TIME_ONLY)
          }
          deadlineAtDate {
            ...StoreFormattedDate @arguments(style: TIME_ONLY)
          }
        }
        ... on TeamScheduleInlineRoutineOccasion {
          taskExecution {
            routineOccasion {
              startAtDate {
                ...StoreFormattedDate @arguments(style: TIME_ONLY)
              }

              deadlineAtDate {
                ...StoreFormattedDate @arguments(style: TIME_ONLY)
              }
            }
          }
        }
        ... on TeamScheduleOneOffEvent {
          startAtDate {
            ...StoreFormattedDate @arguments(style: TIME_ONLY)
          }
          endAtDate {
            ...StoreFormattedDate @arguments(style: TIME_ONLY)
          }
        }
      }
    `,
    props.eventOccasion
  );

  const startAt = useMemo(() => {
    switch (eventOccasion.__typename) {
      case "RoutineOccasion":
        return eventOccasion.startAtDate;
      case "TeamScheduleInlineRoutineOccasion":
        return eventOccasion.taskExecution.routineOccasion.startAtDate;
      case "TeamScheduleOneOffEvent":
        return eventOccasion.startAtDate;
      default:
        return null;
    }
  }, [eventOccasion]);

  const deadlineAt = useMemo(() => {
    switch (eventOccasion.__typename) {
      case "RoutineOccasion":
        return eventOccasion.deadlineAtDate;
      case "TeamScheduleInlineRoutineOccasion":
        return eventOccasion.taskExecution.routineOccasion.deadlineAtDate;
      case "TeamScheduleOneOffEvent":
        return eventOccasion.endAtDate;
      default:
        return null;
    }
  }, [eventOccasion]);

  const theme = useTheme();
  const isDarkTint = Color(props.tint).darken(0.35).isDark();
  const textColor = (() => {
    if (props.status === "active") {
      return isDarkTint ? "text-inverted" : "text-primary";
    }
    if (props.status === "soon") {
      return "text-inverted";
    }
    return "text-secondary";
  })();

  const endTimeTextColor =
    props.status === "soon"
      ? "text-inverted"
      : props.status === "active"
        ? isDarkTint
          ? "text-inverted"
          : "text-primary"
        : "text-washed";

  const intl_tokens = useIntlTokens();

  return (
    <Box spaceLeft="small">
      <Box
        cornerRadius="medium"
        spaceVertical="xsmall"
        spaceHorizontal="xsmall"
        style={{
          width: 45,
          backgroundColor:
            props.status === "active"
              ? props.tint
              : props.status === "soon"
                ? theme.washedText
                : "transparent",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {props.status === "active" ? (
          <AHText type="info-small" color={textColor}>
            {intl_tokens.Now}
          </AHText>
        ) : props.status === "soon" ? (
          <AHText type="info-small" color={textColor}>
            {intl_tokens.Soon}
          </AHText>
        ) : null}
        <AHText
          size={11}
          font={AHTextFonts.BOLD}
          color={textColor}
          spaceVertical="xxsmall"
        >
          {startAt ? <StoreFormattedDate date={startAt} /> : null}
        </AHText>
        {deadlineAt ? (
          <AHText type="detail" color={endTimeTextColor}>
            <StoreFormattedDate date={deadlineAt} />
          </AHText>
        ) : null}
      </Box>
    </Box>
  );
}
